import React, { useState, useEffect } from "react";
import "../Styles/UserLeavePage.css";
import { API_BASE_URL } from "../apiConfig";
import Toast from "../Components/Common/Toast";
import Header from "../Components/Common/Header";
import { CalendarDays, FileText } from "lucide-react";
import Pagination from "../Components/Common/Pagination";

const UserLeavePage = () => {
  const [leaveType, setLeaveType] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [reason, setReason] = useState("");
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [notification, setNotification] = useState({ type: '', message: '' });
  const [isLoading, setIsLoading] = useState(true);
  const [requestedDays, setRequestedDays] = useState(0);
  const [leaveHistory, setLeaveHistory] = useState([]);
  const [isHistoryLoading, setIsHistoryLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10; // or any number you want per page

  const today = new Date().toISOString().split('T')[0];

  useEffect(() => {
    fetchLeaveTypes();
    fetchLeaveHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [leaveHistory]);

  const fetchLeaveHistory = async () => {
    setIsHistoryLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/leaves/user-history`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("idToken")}`,
        },
      });
      
      if (response.status === 401 || response.status === 403) {
        localStorage.clear();
        window.location.href = '/';
        return;
      }
      
      const data = await response.json();
      if (data.success) {
        setLeaveHistory(data.leaves);
      } else {
        showNotification('error', "Failed to fetch leave history.");
      }
    } catch (err) {
      console.error("Error fetching leave history:", err);
      showNotification('error', "Error fetching leave history.");
    } finally {
      setIsHistoryLoading(false);
    }
  };

  const fetchLeaveTypes = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/leaves/types/${localStorage.getItem("companyId")}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("idToken")}`,
        },
      });
        if (response.status === 401 || response.status === 403) {
          localStorage.clear();
          window.location.href = '/';
          return;
        }
      const data = await response.json();
      if (data.success) {
        setLeaveTypes(data.leaveTypes);
      } else {
        showNotification('error', "Failed to fetch leave types.");
      }
    } catch (err) {
      console.error("Error fetching leave types:", err);
      showNotification('error', "Error fetching leave types.");
    } finally {
      setIsLoading(false);
    }
  };

  const showNotification = (type, message) => {
    setNotification({ type, message });
    setTimeout(() => setNotification({ type: '', message: '' }), 5000);
  };

  const calculateDays = (start, end) => {
    if (!start || !end) return;
    const days = Math.ceil((new Date(end) - new Date(start)) / (1000 * 60 * 60 * 24)) + 1;
    setRequestedDays(days);
    return days;
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!leaveType) newErrors.leaveType = "Please select a leave type";
    if (!startDate) newErrors.startDate = "Start date is required";
    if (!endDate) newErrors.endDate = "End date is required";
    
    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      
      if (end < start) {
        newErrors.endDate = "End date cannot be before start date";
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);

    const selectedLeaveType = leaveTypes.find((type) => type.id === leaveType);
    const days = calculateDays(startDate, endDate);

    // Debug logs
    console.log('Leave Request Data:', {
      leaveType,
      startDate,
      endDate,
      reason,
      calculatedDays: days
    });

    if (days > selectedLeaveType.maxDays) {
      setErrors({ 
        endDate: `You cannot apply for more than ${selectedLeaveType.maxDays} days for ${selectedLeaveType.name}.`
      });
      setIsSubmitting(false);
      return;
    }

    try {
      // Debug log before API call
      console.log('Sending leave request to API:', {
        url: `${API_BASE_URL}/leaves/apply`,
        method: 'POST',
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${localStorage.getItem("idToken")}`,
        },
        body: {
          leaveType,
          startDate,
          endDate,
          reason
        }
      });

      const response = await fetch(`${API_BASE_URL}/leaves/apply`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("idToken")}`,
        },
        body: JSON.stringify({
          leaveType,
          startDate,
          endDate,
          reason
        }),
      });

      const data = await response.json();
      
      // Debug log API response
      console.log('API Response:', data);

      if (data.success) {
        showNotification('success', "Leave application submitted successfully!");
        setLeaveType("");
        setStartDate("");
        setEndDate("");
        setReason("");
        fetchLeaveHistory(); // Refresh leave history after successful submission
      } else {
        showNotification('error', "Failed to apply for leave: " + data.message);
      }
    } catch (err) {
      console.error("Error applying for leave:", err);
      showNotification('error', "Error applying for leave. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Helper function to format date from Firestore timestamp
  const formatDate = (timestamp) => {
    if (!timestamp) return "N/A";
    const date = new Date(timestamp._seconds * 1000);
    return date.toLocaleDateString();
  };

  // Helper function to get status badge class
  const getStatusBadgeClass = (status) => {
    switch(status.toLowerCase()) {
      case 'approved': return 'user-leave-status-approved';
      case 'rejected': return 'user-leave-status-rejected';
      case 'pending': return 'user-leave-status-pending';
      default: return '';
    }
  };

  const totalPages = Math.ceil(leaveHistory.length / pageSize);
  const paginatedHistory = leaveHistory.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  return (
    <div className="user-leave-page">
      <div className="leave-page-content">
        <Header title="Leave Management" subtitle="Submit your leave request for approval" icon={<CalendarDays size={28} color="#f3f4f6"/>} />

        <Toast 
          type={notification.type} 
          message={notification.message} 
          onClose={() => setNotification({ type: '', message: '' })} 
        />
        
        <div className="leave-form-container">
          <div className="leave-form-header">
            <h2>Apply for Leave</h2>
          </div>
          
          <form className="leave-form" onSubmit={handleSubmit}>
            <div className="form-info">
              <p>Please fill out the form below to submit your leave request. All fields marked with * are required.</p>
            </div>
            
            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="leaveType">Leave Type *</label>
                <select
                  id="leaveType"
                  value={leaveType}
                  onChange={(e) => setLeaveType(e.target.value)}
                  required
                  className={errors.leaveType ? "error" : ""}
                >
                  <option value="">Select Leave Type</option>
                  {leaveTypes.map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.name} (Available: {type.remainingDays || 0}/{type.maxDays} days)
                    </option>
                  ))}
                </select>
                {errors.leaveType && <div className="error-message">{errors.leaveType}</div>}
              </div>

              <div className="form-group date-range-container">
                <label>Leave Period *</label>
                <div className="date-inputs">
                  <div className="form-group">
                    <label htmlFor="startDate">Start Date</label>
                    <input
                      type="date"
                      id="startDate"
                      value={startDate}
                      min={today}
                      onChange={(e) => {
                        setStartDate(e.target.value);
                        if (endDate) calculateDays(e.target.value, endDate);
                      }}
                      required
                      className={errors.startDate ? "error" : ""}
                    />
                    {errors.startDate && <div className="error-message">{errors.startDate}</div>}
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="endDate">End Date</label>
                    <input
                      type="date"
                      id="endDate"
                      value={endDate}
                      min={startDate || today}
                      onChange={(e) => {
                        setEndDate(e.target.value);
                        if (startDate) calculateDays(startDate, e.target.value);
                      }}
                      required
                      className={errors.endDate ? "error" : ""}
                    />
                    {errors.endDate && <div className="error-message">{errors.endDate}</div>}
                  </div>
                </div>
                
                {requestedDays > 0 && (
                  <div className="days-calculation">
                    Total days: <strong>{requestedDays}</strong>
                  </div>
                )}
              </div>
{/*               
              <div className="form-group full-width">
                <label htmlFor="reason">Reason (Optional)</label>
                <textarea
                  id="reason"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Please provide details about your leave request"
                ></textarea>
              </div> */}
            </div>
            
            <div className="form-footer">
              <button 
                type="submit" 
                className="submit-button"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Submitting..." : "Submit Leave Request"}
              </button>
            </div>
          </form>
        </div>

        {/* Leave History Section */}
        <div className="user-leave-history-card">
          <div className="user-leave-history-header">
            <div className="user-leave-history-title">
              <FileText size={20} />
              <h2>Leave Request History</h2>
            </div>
          </div>
          
          <div className="user-leave-history-content">
            {isHistoryLoading ? (
              <div className="user-leave-history-loading">
                <div className="user-leave-spinner"></div>
                <p>Loading leave history...</p>
              </div>
            ) : leaveHistory.length === 0 ? (
              <div className="user-leave-history-empty">
                <p>You haven't submitted any leave requests yet.</p>
              </div>
            ) : (
              <div className="user-leave-history-table-wrapper">
                <table className="user-leave-history-table">
                  <thead>
                    <tr>
                      <th>Leave Type</th>
                      <th>Period</th>
                      <th>Days</th>
                      
                      <th>Applied On</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedHistory.map((leave) => {
                      const startDate = formatDate(leave.startDate);
                      const endDate = formatDate(leave.endDate);
                      const appliedDate = formatDate(leave.appliedAt);
                      const days = Math.ceil(
                        (new Date(leave.endDate._seconds * 1000) - new Date(leave.startDate._seconds * 1000)) / 
                        (1000 * 60 * 60 * 24) + 1
                      );
                      
                      return (
                        <tr key={leave.id}>
                          <td>{leave.typeName}</td>
                          <td>{startDate} - {endDate}</td>
                          <td>{days}</td>
                          
                          <td>{appliedDate}</td>
                          <td>
                            <span className={`user-leave-status ${getStatusBadgeClass(leave.status)}`}>
                              {leave.status.charAt(0).toUpperCase() + leave.status.slice(1)}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {totalPages > 1 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        )}
      </div>
    </div>
  );
};

export default UserLeavePage;
